// Sign in with Microsoft by sending this same tab to Microsoft and back
// (authorization-code flow with PKCE), instead of reactjs-social-login's
// popup. The popup handed its result back through localStorage and
// window.close(), which silently fails whenever the browser doesn't share
// storage with the popup or won't let it close - the popup then sat on the
// app while the original tab stayed signed out. Its PKCE challenge was also a
// constant baked into the library, which defeats the point of PKCE.
//
// Azure requires redirect_uri to match a registered URI exactly (no query
// string), so the page's own address without its query is used; the query
// (?invitation=..., ?next=...) and router state are saved and put back before
// the app starts (captureMicrosoftReturn, called from index.js).
const KEY = "microsoftSignIn";
const AUTHORIZE_URL = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
const TOKEN_URL = "https://login.microsoftonline.com/common/oauth2/v2.0/token";
const DEFAULT_SCOPE = "profile openid email User.Read";

const base64url = (bytes) =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const randomString = (byteCount) => base64url(crypto.getRandomValues(new Uint8Array(byteCount)));

const challengeFor = async (verifier) =>
  base64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));

export const buildAuthorizeUrl = ({ clientId, redirectUri, scope, state, challenge }) =>
  `${AUTHORIZE_URL}?${new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    redirect_uri: redirectUri,
    response_mode: "query",
    scope,
    state,
    prompt: "select_account",
    code_challenge: challenge,
    code_challenge_method: "S256",
  })}`;

const readSaved = () => {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || "null");
  } catch (e) {
    return null;
  }
};

// Sends this tab to Microsoft's sign-in.
export const startMicrosoftSignIn = async ({ clientId, scope = DEFAULT_SCOPE }) => {
  const verifier = randomString(48);
  const state = randomString(16);
  const redirectUri = window.location.origin + window.location.pathname;
  sessionStorage.setItem(
    KEY,
    JSON.stringify({
      verifier,
      state,
      scope,
      redirectUri,
      search: window.location.search,
      historyState: window.history.state,
    })
  );
  window.location.assign(
    buildAuthorizeUrl({ clientId, redirectUri, scope, state, challenge: await challengeFor(verifier) })
  );
};

// Run once before the app renders. If this page load is Microsoft sending the
// user back, keep the code for takeMicrosoftSignIn and put the address (and
// router state) back as it was before they left.
export const captureMicrosoftReturn = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("state");
    const saved = readSaved();
    if (!state || !saved || saved.state !== state || saved.code || saved.error) return;
    saved.code = params.get("code");
    saved.error = params.get("error") ? params.get("error_description") || params.get("error") : null;
    if (!saved.code && !saved.error) return;
    sessionStorage.setItem(KEY, JSON.stringify(saved));
    window.history.replaceState(saved.historyState, "", window.location.pathname + saved.search);
  } catch (e) {
    // Nothing to restore; the page just loads as it is.
  }
};

// True on the page Microsoft sent the user back to, until takeMicrosoftSignIn runs.
export const hasPendingMicrosoftSignIn = () => {
  const saved = readSaved();
  return Boolean(
    saved && (saved.code || saved.error) && saved.redirectUri === window.location.origin + window.location.pathname
  );
};

// Finishes the sign-in: trades the code for an access token and adds the
// profile (mail, userPrincipalName), the same shape the popup flow resolved
// with. Rejects if Microsoft reported an error or the exchange failed.
export const takeMicrosoftSignIn = async (clientId) => {
  const saved = readSaved();
  sessionStorage.removeItem(KEY);
  if (!saved) throw new Error("No Microsoft sign-in in progress");
  if (saved.error) throw new Error(saved.error);

  const tokenResponse = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
    body: new URLSearchParams({
      client_id: clientId,
      scope: saved.scope,
      code: saved.code,
      redirect_uri: saved.redirectUri,
      grant_type: "authorization_code",
      code_verifier: saved.verifier,
    }),
  }).then((response) => response.json());
  if (!tokenResponse.access_token) {
    throw new Error(tokenResponse.error_description || "Microsoft did not return an access token");
  }

  const profile = await fetch("https://graph.microsoft.com/v1.0/me", {
    headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
  }).then((response) => response.json());
  return { ...profile, ...tokenResponse };
};
